import { Bubble, Conversations, Prompts, Sender, useXAgent, useXChat, Welcome } from '@ant-design/x';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { SyncOutlined } from '@ant-design/icons';
import { type GetProp, message as Toast, Modal, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRootSelector } from '../../../hooks';
import { baseUrl, ChatRequestBody, ChatResp, EventType, ModelType, ModelTypeID } from '../../../api';
import './index.scss';
import { Markdown } from '../../../components';
import { DefaultQuestions } from '../../../utils/defaultQuestions';
import Avatar from '../../../assets/images/avatar.webp';
import AILogo from '../../../assets/images/ai-logo.webp';
import AIAvatar from '../../../assets/images/ai-avatar.webp';
import AIChatAvatar from '../../../assets/images/ai-chat-acatar.webp';
import AgentMan from '../../../assets/images/agent-man.webp';
import AgentHeadTeacher from '../../../assets/images/agent-head-teacher.webp';
import { getValueByProp } from '../../../utils/cu';

const description: any = {
    [ModelType.ASSISTANT_COMMON]: 'Hi～同学们好， 我是你们的专属AI课堂小助手',
    [ModelType.HEAD_TEACHER]: 'Hi～同学们好， 我是你们的专属AI班主任'
};
const placeholderPromptsItems: any = DefaultQuestions;
const CatAIIcon = (
    <span className={'welcome-icon'}>
        <img src={AIChatAvatar} alt={'ai'} />
    </span>
);
const roles: GetProp<typeof Bubble.List, 'roles'> = {
    ai: {
        placement: 'start',
        avatar: {
            icon: CatAIIcon
        },
        styles: {
            content: {
                borderRadius: 16
            }
        }
    },
    local: {
        avatar: {
            icon: <img src={Avatar} alt={''} />
        },
        placement: 'end',
        variant: 'borderless',
        footer: null
    }
};
type PromptsType = {
    id?: string;
    message?: string;
};
const { confirm } = Modal;
export type Props = {
    modelType?: ModelType;
    modelTypeId?: ModelTypeID;
};
const Chats: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation();
    const userInfo = useRootSelector((state) => state.enaea?.userinfo);
    const defaultConversationsItems = localStorage.getItem('conversation-list-assistant') || JSON.stringify([]);
    const conversationsItemsList = JSON.parse(defaultConversationsItems);
    const defaultConversationsItem = {
        key: String(Math.floor(Math.random() * 1024)),
        id: '',
        conversation_id: '',
        label: t('newConversation'),
        type: props?.modelType || ModelType.ASSISTANT_COMMON,
        typeId: props?.modelTypeId || ModelTypeID.ASSISTANT_COMMON,
        senderPromptsItems: [],
        message: []
    };
    const [content, setContent] = React.useState('');
    const [conversationsItems, setConversationsItems] = React.useState(conversationsItemsList);
    const [currentConversation, setCurrentConversation] = useState<typeof defaultConversationsItem>(cloneDeep(defaultConversationsItem));
    const [activeKey, setActiveKey] = React.useState<string | undefined>('');
    const [prompts, setPrompts] = useState<PromptsType | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentMsg, setCurrentMsg] = useState({
        message: '',
        status: '',
        id: '',
        message_id: '',
        conversation_id: '',
        conversation_key: '',
        type: ''
    });
    const [isFirst, setIsFirst] = useState<boolean>(true);
    // ==================== Runtime ====================
    const [agent] = useXAgent<{ data: string }>({
        baseURL: baseUrl + '/chat'
    });
    const confirmHandle = (title: ReactNode, content: ReactNode, onOk: () => void) => {
        confirm({
            getContainer: () => document.querySelector('.enaea_layout') as HTMLElement,
            wrapClassName: 'delete-modal',
            title,
            centered: true,
            icon: null,
            content,
            okText: '确认',
            cancelText: '取消',
            autoFocusButton: null,
            onOk
        });
    };

    const { messages, setMessages } = useXChat({
        agent
    });
    useEffect(() => {
        setPrompts(null);
    }, []);

    const saveAndUpdateConversation = (messages: any[]) => {
        const newConversationsItems = cloneDeep(conversationsItems);
        if (!isFirst && !newConversationsItems.find((item: any) => item.key === currentConversation.key)) {
            currentConversation.label = messages[0].message;
            newConversationsItems.unshift(currentConversation);
        }
        let flag = false;
        for (const item of newConversationsItems) {
            if (item.key === activeKey) {
                const cloneItem = cloneDeep(item);
                item.message = messages;
                cloneItem.message = messages.map(({ id, message, status, role, message_id, conversation_id, type, query }, index) => ({
                    key: id,
                    loading: status === 'loading',
                    role: role === 'local' ? 'local' : 'ai',
                    content: message,
                    message_id: message_id,
                    id: message_id,
                    conversation_id: conversation_id,
                    footer:
                        role === 'local' ? null : status === 'complete' ? (
                            <div
                                className={'re-sync'}
                                onClick={() => {
                                    if (messages[index - 1] && messages[index - 1].id) {
                                        setLoading(true);
                                        setCurrentMsg({
                                            message: '',
                                            status: '',
                                            id: '',
                                            message_id: '',
                                            conversation_id: '',
                                            conversation_key: '',
                                            type: ''
                                        });
                                        createRequest(query || (messages[index - 1].message as string), id, '', type);
                                        return Toast.info(t('success'));
                                    }
                                    return Toast.info(t('fail'));
                                }}
                            >
                                <SyncOutlined style={{ cursor: 'pointer' }} />
                                <span>{t('generation')}</span>
                            </div>
                        ) : null,
                    messageRender: (message: string) => {
                        if (status === 'defaultVideo') {
                            return (
                                <div className={'defaultVideo'}>
                                    <video controls={true} src={'https://ttcdw6-open.obs.cn-north-4.myhuaweicloud.com/ai/ai_zhujiao_xiaoguo.mp4'} />
                                </div>
                            );
                        }
                        if ((item.typeId || item.type) && role !== 'local') {
                            return <Markdown content={message} />;
                        }
                        return message;
                    }
                }));
                flag = true;
                setCurrentConversation(cloneItem);
                break;
            }
        }
        if (flag) {
            localStorage.setItem('conversation-list-assistant', JSON.stringify(newConversationsItems));
            setConversationsItems(newConversationsItems);
        }
    };
    useEffect(() => {
        if (messages && currentConversation) {
            saveAndUpdateConversation(messages);
        }
    }, [messages]);

    const updateChatStatus = (id: string, resultMsg: string, state: string, message_id: string, conversation_id: string) => {
        const cloneMessages = cloneDeep(messages);
        const { status, result } = getValueByProp('id', id, cloneMessages);
        if (status) {
            result.message = resultMsg;
            result.message_id = message_id;
            result.conversation_id = conversation_id;
            result.status = state;
            setMessages(cloneMessages);
        }
    };
    useEffect(() => {
        if (currentMsg.message) {
            updateChatStatus(currentMsg.id, currentMsg.message, currentMsg.status, currentMsg.message_id, currentMsg.conversation_id);
            //@ts-ignore
            if (currentMsg.conversation_key) {
                let flag = false;
                const cloneConversationsItems = cloneDeep(conversationsItems);
                for (const item of cloneConversationsItems) {
                    //@ts-ignore
                    if (item.key === currentMsg.conversation_key) {
                        if (currentMsg.conversation_id) {
                            item.conversation_id = currentMsg.conversation_id;
                            item.id = currentMsg.conversation_id;
                            flag = true;
                        } else {
                            item.conversation_id = '';
                            item.id = '';
                        }
                        break;
                    }
                }
                if (flag) setConversationsItems(cloneConversationsItems);
            }
        }
    }, [currentMsg]);
    const createRequest = (msg: string, nextId: string, sourceId?: string, toolId?: string) => {
        let messageResult = '',
            agent_id = currentConversation?.typeId,
            tool_id = toolId || prompts?.id || '',
            conversation_id = currentConversation?.conversation_id || '',
            deep_think = currentConversation?.type === ModelType.ASSISTANT_COMMON,
            web_search = currentConversation?.type === ModelType.ASSISTANT_COMMON,
            conversation_key = currentConversation.key;
        const body: ChatRequestBody = {
            query: msg,
            agent_id,
            tool_id,
            user_id: userInfo.username || '',
            conversation_id,
            stream: true,
            deep_think,
            web_search
        };
        if (currentConversation.type !== ModelType.ASSISTANT_COMMON) {
            delete body.deep_think;
            delete body.web_search;
            //@ts-ignore
        }
        agent.request(body, {
            onSuccess: () => {
                setLoading(false);
            },
            onUpdate: (message) => {
                if (message.data) {
                    const data: ChatResp = JSON.parse(message.data);
                    if (data.conversation_id && !currentMsg.conversation_id) {
                        setCurrentMsg((prevState) => {
                            if (!currentMsg.conversation_key) currentMsg.conversation_key = conversation_key;
                            prevState.conversation_id = data.conversation_id;
                            return prevState;
                        });
                    }
                    if (data.conversation_id && !currentConversation.conversation_id) {
                        setCurrentConversation((prevState) => {
                            prevState.conversation_id = data.conversation_id;
                            prevState.id = data.conversation_id;
                            return prevState;
                        });
                    }
                    if (data.event === EventType.MESSAGE) {
                        //@ts-ignore
                        const isString = typeof data.answer === 'string';
                        //@ts-ignore
                        if (!isString && typeof data.answer === 'object' && typeof data.answer?.content === 'string') {
                            //@ts-ignore
                            messageResult = data.answer?.content;
                        } else {
                            messageResult += data.answer;
                        }
                        setCurrentMsg({
                            id: nextId,
                            message: messageResult,
                            status: 'complete',
                            message_id: data.message_id,
                            conversation_id: data.conversation_id,
                            //@ts-ignore
                            conversation_key,
                            type: tool_id
                        });
                    } else if (data.event === EventType.MESSAGE_END) {
                        setCurrentMsg({
                            id: nextId,
                            message_id: data.message_id,
                            conversation_id: data.conversation_id,
                            message: messageResult,
                            //@ts-ignore
                            conversation_key,
                            status: 'complete',
                            type: tool_id
                        });
                    }
                    //@ts-ignore
                } else if (message.hasOwnProperty('success') && !message.success) {
                    setLoading(false);
                    setCurrentMsg({
                        id: nextId,
                        message_id: '',
                        conversation_id: '',
                        //@ts-ignore
                        message: message.msg || t('fail'),
                        //@ts-ignore
                        conversation_key,
                        status: 'complete',
                        type: tool_id
                    });
                }
            },
            onError: (error) => {
                console.error(error);
                setCurrentMsg({
                    id: nextId,
                    message_id: '',
                    conversation_id: '',
                    //@ts-ignore
                    conversation_key,
                    //@ts-ignore
                    message: error.msg || t('fail'),
                    status: 'error',
                    type: tool_id
                });
                setLoading(false);
            }
        });
    };

    // ==================== Event ====================
    const onSubmit = (nextContent: string) => {
        if (!nextContent) return;
        setIsFirst(false);
        const len = messages.length;
        if (currentConversation && currentConversation.type === ModelType.PARTNER && userInfo.username === 'GUEST') {
            const newMessages = [
                ...messages,
                {
                    //@ts-ignore
                    role: 'local',
                    //@ts-ignore
                    status: 'complete',
                    id: 'msg_' + activeKey + len,
                    //@ts-ignore
                    message: nextContent,
                    conversation_key: currentConversation.key
                },
                {
                    //@ts-ignore
                    role: 'ai',
                    //@ts-ignore
                    status: '',
                    id: 'msg_' + activeKey + (len + 1),
                    query: nextContent,
                    auth: 'none',
                    //@ts-ignore
                    message: '',
                    conversation_key: currentConversation.key
                }
            ];
            //@ts-ignore
            setMessages(newMessages);
            setContent('');
        } else {
            const nextId = 'msg_' + activeKey + (len + 1);
            const newMessages = [
                ...messages,
                {
                    //@ts-ignore
                    role: 'local',
                    //@ts-ignore
                    status: 'complete',
                    id: 'msg_' + activeKey + len,
                    //@ts-ignore
                    message: nextContent,
                    type: prompts?.id || '',
                    conversation_key: currentConversation.key
                },
                {
                    //@ts-ignore
                    role: 'ai',
                    //@ts-ignore
                    status: 'loading',
                    id: nextId,
                    //@ts-ignore
                    message: '',
                    query: nextContent,
                    conversation_key: currentConversation.key,
                    type: prompts?.id || ''
                }
            ];
            //@ts-ignore
            setMessages(newMessages);
            if (currentConversation.message && !currentConversation.message.length) {
                const newCurrentConversation: typeof defaultConversationsItem = cloneDeep(currentConversation);
                //@ts-ignore
                newCurrentConversation.message = newMessages;
                setCurrentConversation(newCurrentConversation);
                setActiveKey(currentConversation.key);
            }
            setLoading(true);
            setCurrentMsg({
                message: '',
                status: '',
                id: '',
                message_id: '',
                conversation_id: '',
                type: prompts?.id || '',
                conversation_key: ''
            });
            createRequest(nextContent, nextId);
            setContent('');
        }
    };

    const onWelcomePromptsItemClick: GetProp<typeof Prompts, 'onItemClick'> = (info) => {
        const len = messages.length;
        const nextId = 'msg_' + activeKey + (len + 1);
        setIsFirst(false);
        const newMessages = [
            ...messages,
            {
                //@ts-ignore
                role: 'local',
                //@ts-ignore
                status: 'complete',
                id: 'msg_' + activeKey + len,
                //@ts-ignore
                message: info.data.description,
                type: prompts?.id || '',
                conversation_key: currentConversation.key
            },
            {
                //@ts-ignore
                role: 'ai',
                //@ts-ignore
                status: 'defaultVideo',
                id: nextId,
                //@ts-ignore
                message: '',
                type: prompts?.id || '',
                query: info.data.description,
                conversation_key: currentConversation.key
            }
        ];
        setActiveKey(currentConversation.key);
        //@ts-ignore
        setMessages(newMessages);
        setCurrentMsg({
            message: '',
            status: '',
            id: '',
            message_id: '',
            conversation_id: '',
            type: prompts?.id || '',
            conversation_key: currentConversation.key
        });
    };
    const onPromptsItemClick: GetProp<typeof Prompts, 'onItemClick'> = (info) => {
        //@ts-ignore
        if (info.data?.id === prompts?.id) return;
        //@ts-ignore
        setPrompts({ id: info.data.id as string, message: info.data.description as string });
    };

    const onConversationClick: GetProp<typeof Conversations, 'onActiveChange'> = (key) => {
        setActiveKey(key);
        let index = 0,
            flag = false;
        for (const keyElement of conversationsItems) {
            if (keyElement.key === key) {
                flag = true;
                break;
            }
            index++;
        }
        if (flag) {
            setActiveKey(conversationsItems[index].key || '');
            setCurrentConversation(conversationsItems[index]);
            if (conversationsItems[index] && conversationsItems[index].message) {
                setMessages(conversationsItems[index].message);
            }
            if (conversationsItems[index] && conversationsItems[index].senderPromptsItems?.length) {
                const list = conversationsItems[index].senderPromptsItems;
                if (list.length) {
                    const tmp = {
                        id: list[0].id as string,
                        key: list[0].id,
                        label: list[0].description as string,
                        message: list[0].description as string
                    };
                    setPrompts(tmp);
                } else {
                    setPrompts(null);
                }
            }
        }
    };

    const welcomeNode = useMemo(() => {
        return (
            <Space direction="vertical" size={16} className={'chats-placeholder'}>
                <Welcome
                    variant="borderless"
                    icon={null}
                    title={''}
                    description={<span className={'welcome-desc'}>{description[currentConversation?.type]}</span>}
                    extra={null}
                />
                {placeholderPromptsItems[prompts?.id as string] || placeholderPromptsItems[currentConversation?.type] ? (
                    <Prompts
                        className={'welcome-prompts'}
                        title={''}
                        items={placeholderPromptsItems[prompts?.id as string] || placeholderPromptsItems[currentConversation?.type]}
                        styles={{
                            list: {
                                width: '100%'
                            },
                            item: {
                                flex: 1
                            }
                        }}
                        onItemClick={onWelcomePromptsItemClick}
                    />
                ) : null}
            </Space>
        );
    }, [currentConversation?.type, prompts?.id]);

    const deleteConversationHandle = (conversation: typeof defaultConversationsItem) => {
        const callback = () => {
            let cloneList = cloneDeep(conversationsItems);
            let i = 0,
                flag = false;
            for (const item of cloneList) {
                if (item.id === conversation.id) {
                    flag = true;
                    break;
                }
                i++;
            }
            if (flag) {
                cloneList.splice(i, 1);
                setConversationsItems(cloneList);
                const cloneDefault = cloneDeep(defaultConversationsItem);
                setCurrentConversation(cloneDefault);
                setActiveKey('');
                setMessages([]);
                localStorage.setItem('conversation-list-assistant', JSON.stringify(cloneList));
                //@ts-ignore
                Toast.info(t('success'));
            }
        };
        confirmHandle('删除对话记录', '此操作将永久删除您创建的所有聊天记录，且无法撤销。' + '请确认以继续。', callback);
    };
    const usePlaceholder = useMemo(() => {
        const defaultPlaceholder: any = {
            [ModelType.ASSISTANT_COMMON]: '您好！我是您的AI助手，请问需要什么帮助？'
        };
        return defaultPlaceholder[prompts?.id as string] || defaultPlaceholder[currentConversation?.type] || '';
    }, [currentConversation?.type, prompts?.id]);
    const userPrompts = useMemo(() => {
        return currentConversation?.senderPromptsItems.map((item: any) => (
            <span
                className={'sender-prompts-item ' + (prompts?.id === item.id ? 'active' : '')}
                key={item.key || item.id}
                onClick={() => onPromptsItemClick({ data: item })}
            >
                {item.label}
            </span>
        ));
    }, [currentConversation?.senderPromptsItems, prompts?.id]);

    return (
        <div className={'assistant chats-layout'}>
            <div className={'chats-menu'}>
                {/* 🌟 logo */}
                <div className={'chats-logo'}>
                    <img src={AIAvatar} draggable={false} alt={'ai'} />
                    <img src={AILogo} draggable={false} alt="logo" />
                    <span>{t(props.modelType === ModelType.HEAD_TEACHER ? 'aiHeadTeacher.title' : 'assistant.name')}</span>
                </div>
                {/* 🌟 会话管理 */}
                <div className={'recent-conversations'}>
                    <span className={'title'}>{t('recentConversations')}</span>
                    <Conversations
                        items={conversationsItems}
                        className={'chats-conversations'}
                        activeKey={activeKey}
                        onActiveChange={onConversationClick}
                        menu={(conversation) => {
                            return {
                                trigger: () => (
                                    <div className={'conversation-item-op'}>
                                        <i
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                deleteConversationHandle(conversation as typeof defaultConversationsItem);
                                            }}
                                            className={'iconfont icon-agent-delete conversation-item-delete'}
                                        />
                                    </div>
                                ),
                                items: []
                            };
                        }}
                    />
                </div>
                <div className={'agent-user'}>
                    <img width={120} height={159} src={props.modelType === ModelType.HEAD_TEACHER ? AgentHeadTeacher : AgentMan} alt={'agent-user'} />
                </div>
            </div>
            <div className={'chats-chat'}>
                {/* 🌟 消息列表 */}
                <Bubble.List
                    key={currentConversation.conversation_id}
                    items={
                        currentConversation?.message?.length > 0
                            ? currentConversation?.message
                            : [
                                  {
                                      content: welcomeNode,
                                      variant: 'borderless',
                                      avatar: { icon: CatAIIcon }
                                  }
                              ]
                    }
                    roles={roles}
                    className={'chats-messages'}
                />
                {/* 🌟 提示词 */}
                <div className={'sender-prompts-items'}>{userPrompts}</div>
                {/* 🌟 输入框 */}
                <Sender
                    placeholder={usePlaceholder}
                    value={content}
                    footer={null}
                    onSubmit={onSubmit}
                    onChange={setContent}
                    loading={loading}
                    className={'chats-sender'}
                />
            </div>
        </div>
    );
};
export default Chats;
